import { Client, createClient } from '@connectrpc/connect'
import { createGrpcWebTransport } from '@connectrpc/connect-web'
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'
import * as React from 'react'
import { useAuth } from 'react-oidc-context'
import { ApplicationInformationService } from '@/generated/Services/ApplicationInformationService_pb'
import { BusinessLocationService } from '@/generated/Services/BusinessLocationService_pb'
import { BusinessService } from '@/generated/Services/BusinessService_pb'
import { ConfigurationService } from '@/generated/Services/ConfigurationService_pb'
import { ProjectService } from '@/generated/Services/ProjectService_pb'
import { ReportService } from '@/generated/Services/ReportService_pb'
import { RoleService } from '@/generated/Services/RoleService_pb'
import { TrackingKindService } from '@/generated/Services/TrackingKindService_pb'
import { TrackingService } from '@/generated/Services/TrackingService_pb'
import { UserBusinessLocationService } from '@/generated/Services/UserBusinessLocationService_pb'
import { UserProjectService } from '@/generated/Services/UserProjectService_pb'
import { UserRoleService } from '@/generated/Services/UserRoleService_pb'
import { UserService } from '@/generated/Services/UserService_pb'
import { UserWorkingTimeModelService } from '@/generated/Services/UserWorkingTimeModelService_pb'
import { WorkingTimeModelService } from '@/generated/Services/WorkingTimeModelService_pb'

/**
 * Contains all available grpc clients needed for the application.
 */
export interface GrpcClientsContextValue {
    applicationInformation: Client<typeof ApplicationInformationService>
    business: Client<typeof BusinessService>
    businessLocation: Client<typeof BusinessLocationService>
    configuration: Client<typeof ConfigurationService>
    project: Client<typeof ProjectService>
    report: Client<typeof ReportService>
    role: Client<typeof RoleService>
    tracking: Client<typeof TrackingService>
    trackingKind: Client<typeof TrackingKindService>
    user: Client<typeof UserService>
    userBusinessLocation: Client<typeof UserBusinessLocationService>
    userRole: Client<typeof UserRoleService>
    userWorkingTimeModel: Client<typeof UserWorkingTimeModelService>
    userProject: Client<typeof UserProjectService>
    workingTimeModel: Client<typeof WorkingTimeModelService>
}

const Context = React.createContext<GrpcClientsContextValue | null>(null)

export const useGrpcClients = (): Partial<GrpcClientsContextValue> =>
    React.use(Context) ?? {}

export const GrpcClientContext: React.FC<React.PropsWithChildren> = ({
    children,
}) => {
    const auth = useAuth()
    const appInsights = useAppInsightsContext()

    const clients = React.useMemo<GrpcClientsContextValue>(
        () => {
            const transport = createGrpcWebTransport({
                baseUrl: import.meta.env.VITE_BACKEND_URL,
                interceptors: [
                    next => async req => {
                        // If the `access_token` is available, set the corresponding header.
                        // In theory, when you look at the order of components in the tree, this should never be `undefined`.
                        if (auth.user?.access_token) {
                            req.header.set(
                                'Authorization',
                                `Bearer ${auth.user.access_token}`
                            )
                        }
                        // If the token expires in less than 30 seconds, refresh it before using it.
                        if ((auth.user?.expires_in ?? 0) < 30) {
                            const u = await auth.signinSilent()
                            appInsights.trackEvent({
                                name: 'Auth Token Refreshed',
                                properties: {
                                    expires_in: auth.user?.expires_in,
                                },
                            })

                            if (u?.access_token) {
                                req.header.set(
                                    'Authorization',
                                    `Bearer ${u.access_token}`
                                )
                            }
                        }

                        return await next(req)
                    },
                ],
            })

            return {
                applicationInformation: createClient(
                    ApplicationInformationService,
                    transport
                ),
                business: createClient(BusinessService, transport),
                businessLocation: createClient(
                    BusinessLocationService,
                    transport
                ),
                configuration: createClient(ConfigurationService, transport),
                project: createClient(ProjectService, transport),
                report: createClient(ReportService, transport),
                role: createClient(RoleService, transport),
                tracking: createClient(TrackingService, transport),
                trackingKind: createClient(TrackingKindService, transport),
                user: createClient(UserService, transport),
                userBusinessLocation: createClient(
                    UserBusinessLocationService,
                    transport
                ),
                userRole: createClient(UserRoleService, transport),
                userProject: createClient(UserProjectService, transport),
                userWorkingTimeModel: createClient(
                    UserWorkingTimeModelService,
                    transport
                ),
                workingTimeModel: createClient(
                    WorkingTimeModelService,
                    transport
                ),
            }
        },
        // Recreate clients whenever the `access_token` changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [auth]
    )

    // While the authentication is still loading, don't display anything yet.
    return <Context value={clients}>{children}</Context>
}
